import React, { ReactNode, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

interface DataViewEmptyStateProps {
  icon?: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
  className?: string;
}

export function DataViewEmptyState({
  icon,
  title,
  description,
  action,
  className
}: DataViewEmptyStateProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center h-full text-center py-12", className)}>
      {icon && (
        <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
          {icon}
        </div>
      )}
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        {title}
      </h3>
      <p className="text-sm text-gray-600 mb-6 max-w-sm">
        {description}
      </p>
      {action}
    </div>
  );
}

interface DataViewLoadMoreProps {
  onLoadMore: () => void;
  isLoadingMore: boolean;
  hasMore: boolean;
  className?: string;
}

export function DataViewLoadMore({
  onLoadMore,
  isLoadingMore,
  hasMore,
  className
}: DataViewLoadMoreProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  const loadRequestedRef = useRef(false);
  const loadingStartedRef = useRef(false);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    if (isLoadingMore) {
      loadingStartedRef.current = true;
      return;
    }

    if (loadingStartedRef.current) {
      loadRequestedRef.current = false;
      loadingStartedRef.current = false;
    }
  }, [isLoadingMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || isLoadingMore || loadRequestedRef.current) return;

        loadRequestedRef.current = true;
        onLoadMoreRef.current();
      },
      {
        rootMargin: '0px 0px 480px 0px',
        threshold: 0,
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore]);

  useEffect(() => {
    if (!hasMore) {
      loadRequestedRef.current = false;
      loadingStartedRef.current = false;
    }
  }, [hasMore]);

  if (!hasMore) return null;

  return (
    <div
      ref={sentinelRef}
      className={cn("flex min-h-24 items-center justify-center py-6", className)}
      aria-live="polite"
      aria-busy={isLoadingMore}
    >
      {isLoadingMore ? (
        <div className="inline-flex items-center gap-2 text-sm font-medium text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Lädt...</span>
        </div>
      ) : (
        <span className="sr-only">Weitere Einträge werden beim Scrollen geladen.</span>
      )}
    </div>
  );
}
